import React, {useCallback} from 'react';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {
    useCurrencies,
    useDateFormats,
    useDecimalFormats,
    usePaperFormats,
    useSymbolPositions,
    useThousandFormats,
} from 'src/hooks/dictionaries';
import {Locale} from 'src/openapi/admin/models/Locale';
import {LocaleTimezone} from 'src/openapi/admin/models/LocaleTimezone';

import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {INTEGER} from '@pb/kit-formatters';


import {FormatSettingsSkeleton} from './format-settings-skeleton';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {DateFormat} from 'src/openapi/admin/models/DateFormat';
import type {DecimalFormat} from 'src/openapi/admin/models/DecimalFormat';
import type {Error} from 'src/openapi/admin/models/Error';
import type {PaperFormat} from 'src/openapi/admin/models/PaperFormat';
import type {SupportedCurrency} from 'src/openapi/admin/models/SupportedCurrency';
import type {SymbolPosition} from 'src/openapi/admin/models/SymbolPosition';
import type {ThousandFormat} from 'src/openapi/admin/models/ThousandFormat';

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    locale?: Locale,
    defaultLocale?: Locale,
    onChange: (locale: Locale) => void,
    disabled?: boolean,
    onFieldChange?: (parameterName: string) => void,
    withTimeZone?: boolean,
};

type OnCheckBoxChange = (data: {event: React.SyntheticEvent<HTMLInputElement, Event>, name: string}) => void;

const SECTION_NAME = 'Format Settings';

export const FormatSettingsSection: React.FC<Props> = props => {
    const {
        isLoading,
        loadingError,
        defaultLocale,
        locale,
        onChange,
        disabled,
        onFieldChange,
        withTimeZone,
    } = props;

    const {currencies} = useCurrencies();
    const {symbolPositions} = useSymbolPositions();
    const {decimalFormats} = useDecimalFormats();
    const {thousandFormats} = useThousandFormats();
    const {dateFormats} = useDateFormats();
    const {paperFormats} = usePaperFormats();

    const onChangeCustomSettings = useCallback<OnCheckBoxChange>(changeData => {
        onFieldChange?.('Use custom settings');
        if (locale && defaultLocale) {
            const isCustomSettings = changeData.event.currentTarget.checked;

            onChange({...locale, isCustomSettings});
        }
    }, [locale, defaultLocale, onChange, onFieldChange]);

    const onChangeSymbolPosition = useCallback((symbolPosition: SymbolPosition) => {
        onFieldChange?.('Currency symbol');
        if (locale) {
            onChange({...locale, symbolPosition});
        }
    }, [locale, onChange, onFieldChange]);

    const onChangeCurrency = useCallback((currency: SupportedCurrency) => {
        onFieldChange?.('Currency');
        if (locale) {
            onChange({...locale, currency});
        }
    }, [locale, onChange, onFieldChange]);

    const onChangeCurrencyHover = useCallback((nativeCurrencyHover: boolean) => {
        onFieldChange?.('Native currency hover');
        if (locale) {
            onChange({...locale, nativeCurrencyHover});
        }
    }, [locale, onChange, onFieldChange]);

    const onChangeDecimalFormat = useCallback((decimalFormat: DecimalFormat) => {
        onFieldChange?.('Decimal format');
        if (locale) {
            onChange({...locale, decimalFormat});
        }
    }, [locale, onChange, onFieldChange]);

    const onChangeThousandFormat = useCallback((thousandFormat: ThousandFormat) => {
        onFieldChange?.('Thousand format');
        if (locale) {
            onChange({...locale, thousandFormat});
        }
    }, [locale, onChange, onFieldChange]);

    const onChangeDateFormat = useCallback((dateFormat: DateFormat) => {
        onFieldChange?.('Date format');
        if (locale) {
            onChange({...locale, dateFormat});
        }
    }, [locale, onChange, onFieldChange]);

    const onChangePaperFormat = useCallback((paperFormat: PaperFormat) => {
        onFieldChange?.('Paper format');
        if (locale) {
            onChange({...locale, paperFormat});
        }
    }, [locale, onChange, onFieldChange]);

    const onChangeTimeZone = useCallback((timezone: LocaleTimezone) => {
        onFieldChange?.('Time Zone Region');
        if (locale) {
            onChange({...locale, timezone});
        }
    }, [locale, onChange, onFieldChange]);

    return (
        <Skeleton shown={isLoading} view={<FormatSettingsSkeleton/>}>
            <ServerResponseHandler
                smallView={true}
                sectionName={SECTION_NAME}
                serverError={loadingError}
                accessRight={ACCESS_RIGHTS.ACCOUNT_SCREEN_VIEW as AccessRight}>
                <FormSection headline={SECTION_NAME}>
                    <Group direction="v" justify="start">
                        <CheckBox {...{
                            label: 'Use custom settings',
                            disabled,
                            checked: locale?.isCustomSettings,
                            name: 'localeCustomSettings',
                            onCheckBoxChange: onChangeCustomSettings,
                        }}
                        />
                        <Group justify="start">
                            <Group style={{paddingRight: 16, paddingBottom: 16}}>
                                <Group direction="v">
                                    <div style={{width: 300}}>
                                        <Label>Currency</Label>
                                        <ComboBox
                                            disabled={disabled || !locale?.isCustomSettings}
                                            value={locale?.currency}
                                            onChange={onChangeCurrency}>
                                            {currencies.map(({code, description, symbol}) => (
                                                <ComboboxItem
                                                    key={code}
                                                    caption={description}
                                                    value={code}
                                                    meta={symbol}
                                                />
                                            ))}
                                        </ComboBox>
                                    </div>
                                    <div style={{width: 300}}>
                                        <Label>Decimal format</Label>
                                        <ComboBox
                                            disabled={disabled || !locale?.isCustomSettings}
                                            value={locale?.decimalFormat}
                                            onChange={onChangeDecimalFormat}>
                                            {decimalFormats.map(({code, description}) => (
                                                <ComboboxItem key={code} caption={description} value={code}/>
                                            ))}
                                        </ComboBox>
                                    </div>
                                    <div style={{width: 300}}>
                                        <Label>Date format</Label>
                                        <ComboBox
                                            disabled={disabled || !locale?.isCustomSettings}
                                            value={locale?.dateFormat}
                                            onChange={onChangeDateFormat}>
                                            {dateFormats.map(({code, description}) => (
                                                <ComboboxItem key={code} caption={description} value={code}/>
                                            ))}
                                        </ComboBox>
                                    </div>
                                </Group>
                            </Group>
                            <Group style={{paddingRight: 16, paddingBottom: 16}}>
                                <Group direction="v">
                                    <div style={{width: 300}}>
                                        <Label>Currency symbol</Label>
                                        <ComboBox
                                            disabled={disabled || !locale?.isCustomSettings}
                                            value={locale?.symbolPosition}
                                            onChange={onChangeSymbolPosition}>
                                            {symbolPositions.map(({code, description}) => (
                                                <ComboboxItem key={code} caption={description} value={code}/>
                                            ))}
                                        </ComboBox>
                                    </div>
                                    <div style={{width: 300}}>
                                        <Label>Thousand format</Label>
                                        <ComboBox
                                            disabled={disabled || !locale?.isCustomSettings}
                                            value={locale?.thousandFormat}
                                            onChange={onChangeThousandFormat}>
                                            {thousandFormats.map(({code, description}) => (
                                                <ComboboxItem
                                                    key={code}
                                                    caption={description}
                                                    value={code}
                                                    meta={
                                                        INTEGER.format(1000, {thousandFormat: code, decimalFormat: ''})
                                                    }
                                                />
                                            ))}
                                        </ComboBox>
                                    </div>
                                    <div style={{width: 300}}>
                                        <Label>Paper format</Label>
                                        <ComboBox
                                            disabled={disabled || !locale?.isCustomSettings}
                                            value={locale?.paperFormat}
                                            onChange={onChangePaperFormat}>
                                            {paperFormats.map(({code, description}) => (
                                                <ComboboxItem key={code} caption={description} value={code}/>
                                            ))}
                                        </ComboBox>
                                    </div>
                                </Group>
                            </Group>
                            <Group style={{paddingRight: 16, paddingBottom: 16}}>
                                <Group direction="v">
                                    <div style={{width: 300}}>
                                        <Label>Native currency hover</Label>
                                        <ComboBox
                                            disabled={disabled || !locale?.isCustomSettings}
                                            value={locale?.nativeCurrencyHover}
                                            onChange={onChangeCurrencyHover}>
                                            <ComboboxItem key="Enabled" caption="Enabled" value={true}/>
                                            <ComboboxItem key="Disabled" caption="Disabled" value={false}/>
                                        </ComboBox>
                                    </div>
                                    {withTimeZone ? (
                                        <div style={{width: 300}}>
                                            <Label>Time Zone Region</Label>
                                            <ComboBox
                                                disabled={disabled || !locale?.isCustomSettings}
                                                value={locale?.timezone}
                                                onChange={onChangeTimeZone}>
                                                {Object.values(LocaleTimezone).map(timezone => (
                                                    <ComboboxItem key={timezone} caption={timezone} value={timezone}/>
                                                ))}
                                            </ComboBox>
                                        </div>
                                    ) : null}
                                </Group>
                            </Group>
                        </Group>
                    </Group>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
